import {TaskLeadingVisual} from '@github-ui/agent-sessions/components/TaskLeadingVisual'
import {useTask} from '@github-ui/agent-sessions/hooks/use-task'
import type {SessionState} from '@github-ui/agent-sessions/types/session'
import {isSessionState} from '@github-ui/agent-sessions/types/session'
import {generateTaskPathForRepo} from '@github-ui/agent-sessions/utils/generate-task-path'
import {getAPITaskState} from '@github-ui/agent-sessions/utils/task-helpers'
import {AgentIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {type FC, useMemo} from 'react'

export interface FixTaskInfo {
  taskId: string
  state: SessionState
}

const fixTaskStatusMap: Record<string, {label: string; color: string}> = {
  in_progress: {label: 'Fixing…', color: 'color-fg-attention'},
  queued: {label: 'Fixing…', color: 'color-fg-attention'},
  completed: {label: 'Fixed', color: 'color-fg-success'},
  failed: {label: 'Fix failed', color: 'color-fg-danger'},
  timed_out: {label: 'Fix failed', color: 'color-fg-danger'},
  cancelled: {label: 'Fix cancelled', color: 'color-fg-danger'},
}

export const FixFindingStatus: FC<{
  resolved: boolean
  taskBasePath: string
  taskId?: string
  onFix: () => void
}> = ({resolved, taskId, taskBasePath, onFix}) => {
  const effectiveTaskId = resolved ? undefined : taskId
  const {data, isError} = useTask({taskId: effectiveTaskId})

  const fixTask: FixTaskInfo | undefined = useMemo(() => {
    if (!effectiveTaskId) return undefined
    if (isError) return {taskId: effectiveTaskId, state: 'failed' as SessionState}
    if (!data) return {taskId: effectiveTaskId, state: 'queued' as SessionState}
    const rawState = getAPITaskState(data)
    const state: SessionState = isSessionState(rawState) ? rawState : 'queued'
    return {taskId: effectiveTaskId, state}
  }, [effectiveTaskId, data, isError])

  if (resolved) return null

  if (!fixTask) {
    return (
      <Button leadingVisual={AgentIcon} onClick={onFix}>
        Fix with Copilot
      </Button>
    )
  }

  const status = fixTaskStatusMap[fixTask.state]
  if (!status) return null

  const canRetry = fixTask.state === 'failed' || fixTask.state === 'timed_out' || fixTask.state === 'cancelled'
  const taskUrl = generateTaskPathForRepo({repositoryNwo: taskBasePath.replace(/^\//, ''), taskId: fixTask.taskId})

  return (
    <>
      <span role="status" className={`d-flex flex-items-center gap-1 ${status.color}`}>
        <TaskLeadingVisual sessionState={fixTask.state} />
        {taskUrl ? (
          <a href={taskUrl} className={`text-small ${status.color}`}>
            {status.label}
          </a>
        ) : (
          <span className="text-small">{status.label}</span>
        )}
      </span>
      {canRetry && (
        <Button leadingVisual={AgentIcon} onClick={onFix}>
          Retry fix
        </Button>
      )}
    </>
  )
}
